// src/components/layout/Sidebar.jsx
import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import postMelogo from '@assets/images/PostMelogo.png'
import { useAuthStore } from '@store/useAuthStore';
import authApi from '@services/authApi';

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedMenus, setExpandedMenus] = useState([]);
  const [activeItems, setActiveItems] = useState([]);
  
  // 인증 상태 가져오기
  const { user, logout: authLogout } = useAuthStore();

  useEffect(() => {
    // 모달 매니저가 로드되었는지 확인
    if (!window.modalManager) {
      console.error('Modal manager is not loaded');
    }
  }, []);

  // 현재 페이지 URL에서 경로명 추출
  const getCurrentPage = () => {
    const path = location.pathname;
    // 슬래시로 시작하는 경로에서 첫 번째 슬래시 이후의 문자열 추출
    const page = path.split('/')[1] || 'dashboard';
    return page;
  };

  // 메뉴 활성화 함수
  const activateMenu = () => {
    const currentPage = getCurrentPage();
    const newActiveItems = [];

    // 기본 메뉴 항목 확인
    const menuPage = menuPageMapping[currentPage] || currentPage;
    newActiveItems.push(menuPage);

    // 서브메뉴 항목 확인 및 부모 메뉴 확장
    if (submenuMapping[currentPage]) {
      const parentMenu = submenuMapping[currentPage].parent;
      newActiveItems.push(parentMenu);
      
      // 부모 메뉴 확장
      if (!expandedMenus.includes(parentMenu)) {
        setExpandedMenus([...expandedMenus, parentMenu]);
      }
    }

    setActiveItems(newActiveItems);
  };

  // URL 변경 시 메뉴 활성화 상태 업데이트
  useEffect(() => {
    activateMenu();
  }, [location.pathname]);

  // 서브메뉴 토글 함수
  const toggleSubmenu = (menuId) => {
    // 이미 확장된 메뉴인 경우 축소
    if (expandedMenus.includes(menuId)) {
      setExpandedMenus(expandedMenus.filter(id => id !== menuId));
    } else {
      // 확장되지 않은 메뉴인 경우 확장 (다른 메뉴는 모두 축소)
      setExpandedMenus([menuId]);
      
      // 상위 메뉴 활성화
      setActiveItems([menuId]);
      
      // 첫 번째 하위 메뉴 항목으로 이동
      const firstSubmenuPage = getFirstSubmenuPage(menuId);
      if (firstSubmenuPage) {
        navigate(`/${firstSubmenuPage}`);
        // 하위 메뉴 항목과 상위 메뉴를 활성화
        setActiveItems([firstSubmenuPage, menuId]);
      }
    }
  };

  // 메뉴 ID에 해당하는 첫 번째 하위 메뉴 페이지를 반환하는 함수
  const getFirstSubmenuPage = (menuId) => {
    if (menuId === 'auth-management') {
      return 'account-list';
    } else if (menuId === 'data-management') {
      return 'design-management';
    } else if (menuId === 'reprint-management') {
      return 'media-list';
    } else if (menuId === 'user-menu') {
      return 'page-preset-management';
    }
    return null;
  };

  // 일반 메뉴 클릭 핸들러
  const handleMenuClick = (menuId) => {
    // 서브메뉴가 열려있으면 닫기
    if (expandedMenus.length > 0) {
      setExpandedMenus([]);
    }
    // 메뉴 활성화 (서브메뉴의 active 상태 제거)
    setActiveItems([menuId]);
  };

  // 로그아웃 핸들러
  const handleLogout = () => {
    if (window.modalManager) {
      window.modalManager.confirm('로그아웃 하시겠습니까?', {
        title: '로그아웃',
        cancelText: '아니오',
        confirmText: '예',
        onConfirm: async () => {
          try {
            // 백엔드 로그아웃 API 호출
            await authApi.logout();
            
            // 로컬 스토리지 및 상태 초기화
            authLogout();
            
            // 로그인 페이지로 이동
            navigate('/login');
          } catch (error) {
            console.error('로그아웃 실패:', error);
            // 에러가 발생해도 로컬에서는 로그아웃 처리
            authLogout();
            navigate('/login');
          }
        }
      });
    }
  };

  // 페이지와 메뉴 ID 매핑
  const menuPageMapping = {
    '': 'dashboard',
    'dashboard': 'dashboard',
    'reprint': 'reprint',
    'error-log': 'error-log',
    'device': 'device',
    'device-setting': 'device-setting',
    'qr-server': 'qr-server',
    'sales': 'sales',
    'manual': 'manual',
    'software': 'software',
    'user-menu': 'page-preset-management'
  };

  // 서브메뉴 매핑 (페이지 -> 부모 메뉴)
  const submenuMapping = {
    'account-list': { parent: 'auth-management' },
    'account-group-management': { parent: 'auth-management' },
    'design-management': { parent: 'data-management' },
    'camera-angle': { parent: 'data-management' },
    'filter': { parent: 'data-management' },
    'layout': { parent: 'data-management' },
    'media-list': { parent: 'reprint-management' },
    'reprint': { parent: 'reprint-management' },
    'page-preset-management': { parent: 'user-menu' }
  };

  // 메뉴 항목이 활성화되었는지 확인
  const isMenuActive = (menuId) => {
    return activeItems.includes(menuId);
  };

  // 메뉴 항목이 확장되었는지 확인
  const isMenuExpanded = (menuId) => {
    return expandedMenus.includes(menuId);
  };

  return (
    <>
      {/*사이드바 시작*/}
      <div className="sidebar">
        <div className="sidebar-header">
          <div className="logo">
            <img src={postMelogo} alt="Logo" />
          </div>

          <div className="user-notification-container">
            <div className="account-info">
              <span className="account-name">{user?.name || user?.email || '사용자'}</span>
            </div>

            <div className="notification">
              <i className="ri-notification-3-line"></i>
              <div className="badge">
                <span>3</span>
              </div>
            </div>
          </div>
          <div className="logout-button" onClick={handleLogout}>
            <i className="ri-logout-box-line"></i>
            <span>로그아웃</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <ul>
            <li 
              data-page="dashboard" 
              className={isMenuActive('dashboard') ? 'active' : ''}
              onClick={() => handleMenuClick('dashboard')}
            >
              <NavLink to="/dashboard">
                <i className="ri-line-chart-line"></i>
                <span>대시보드</span>
              </NavLink>
            </li>
            <li 
              className={`has-submenu ${isMenuActive('reprint-management') ? 'active' : ''} ${isMenuExpanded('reprint-management') ? 'expanded' : ''}`} 
              data-page="reprint-management"
            >
              <div 
                className="menu-item"
                onClick={() => toggleSubmenu('reprint-management')}
              >
                <i className="ri-printer-line"></i>
                <span>재프린트</span>
                <i className={`${isMenuExpanded('reprint-management') ? 'ri-arrow-right-s-fill' : 'ri-arrow-up-s-fill'} expand-icon`}></i>
              </div>

              <ul className={`submenu ${isMenuExpanded('reprint-management') ? 'active' : ''}`}>
                <li 
                  data-page="media-list"
                  className={isMenuActive('media-list') ? 'active' : ''}
                >
                  <NavLink to="/media-list">
                    <span>미디어리스트</span>
                  </NavLink>
                </li>
                <li 
                  data-page="reprint"
                  className={isMenuActive('reprint') ? 'active' : ''}
                >
                  <NavLink to="/reprint">
                    <span>리프린트</span>
                  </NavLink>
                </li>
              </ul>
            </li>
            <li 
              data-page="error-log" 
              className={isMenuActive('error-log') ? 'active' : ''}
              onClick={() => handleMenuClick('error-log')}
            >
              <NavLink to="/error-log">
                <i className="ri-error-warning-line"></i>
                <span>오류로그</span>
              </NavLink>
            </li>

            <li 
              className={`has-submenu ${isMenuActive('auth-management') ? 'active' : ''} ${isMenuExpanded('auth-management') ? 'expanded' : ''}`} 
              data-page="auth-management"
            >
              <div 
                className="menu-item2"
                onClick={() => toggleSubmenu('auth-management')}
              >
                <i className="ri-user-settings-line"></i>
                <span>권한 관리</span>
                <i className={`${isMenuExpanded('auth-management') ? 'ri-arrow-right-s-fill' : 'ri-arrow-up-s-fill'} expand-icon`}></i>
              </div>

              <ul className={`submenu ${isMenuExpanded('auth-management') ? 'active' : ''}`}>
                <li 
                  data-page="account-list"
                  className={isMenuActive('account-list') ? 'active' : ''}
                >
                  <NavLink to="/account-list">
                    <span>계정목록</span>
                  </NavLink>
                </li>
                <li 
                  data-page="account-group-management"
                  className={isMenuActive('account-group-management') ? 'active' : ''}
                >
                  <NavLink to="/account-group-management">
                    <span>계정권한 그룹 관리</span>
                  </NavLink>
                </li>
              </ul>
            </li>

            <li 
              className={`has-submenu ${isMenuActive('data-management') ? 'active' : ''} ${isMenuExpanded('data-management') ? 'expanded' : ''}`} 
              data-page="data-management"
            >
              <div 
                className="menu-item"
                onClick={() => toggleSubmenu('data-management')}
              >
                <i className="ri-database-line"></i>
                <span>데이터 관리</span>
                <i className={`${isMenuExpanded('data-management') ? 'ri-arrow-right-s-fill' : 'ri-arrow-up-s-fill'} expand-icon`}></i>
              </div>

              <ul className={`submenu ${isMenuExpanded('data-management') ? 'active' : ''}`}>
                <li 
                  data-page="design-management"
                  className={isMenuActive('design-management') ? 'active' : ''}
                >
                  <NavLink to="/design-management">
                    <span>페이지 디자인 테마 관리</span>
                  </NavLink>
                </li>
                <li 
                  data-page="camera-angle-management"
                  className={isMenuActive('camera-angle-management') ? 'active' : ''}
                >
                  <NavLink to="/camera-angle-management">
                    <span>카메라 앵글 관리</span>
                  </NavLink>
                </li>

                <li 
                  data-page="photo-layout-management"
                  className={isMenuActive('photo-layout-management') ? 'active' : ''}
                >
                  <NavLink to="/photo-layout-management">
                    <span>포토 레이아웃 관리</span>
                  </NavLink>
                </li>

                <li 
                  data-page="photo-filter-management"
                  className={isMenuActive('photo-filter-management') ? 'active' : ''}
                >
                  <NavLink to="/photo-filter-management">
                    <span>포토 필터 관리</span>
                  </NavLink>
                </li>
                <li 
                  data-page="photo-frame-management"
                  className={isMenuActive('photo-frame-management') ? 'active' : ''}
                >
                  <NavLink to="/photo-frame-management">
                    <span>포토 프레임 관리</span>
                  </NavLink>
                </li>
              </ul>
            </li>
            <li 
              data-page="device"
              className={isMenuActive('device') ? 'active' : ''}
              onClick={() => handleMenuClick('device')}
            >
              <NavLink to="/device">
                <i className="ri-device-line"></i>
                <span>기기 관리</span>
              </NavLink>
            </li>
            <li 
              className={`has-submenu ${isMenuActive('user-menu') ? 'active' : ''} ${isMenuExpanded('user-menu') ? 'expanded' : ''}`} 
              data-page="user-menu"
            >
              <div 
                className="menu-item2"
                onClick={() => toggleSubmenu('user-menu')}
              >
                <i className="ri-file-edit-line"></i>
                <span>사용자메뉴 관리</span>
                <i className={`${isMenuExpanded('user-menu') ? 'ri-arrow-right-s-fill' : 'ri-arrow-up-s-fill'} expand-icon`}></i>
              </div>

              <ul className={`submenu ${isMenuExpanded('user-menu') ? 'active' : ''}`}>
                <li 
                  data-page="page-preset-management"
                  className={isMenuActive('page-preset-management') ? 'active' : ''}
                >
                  <NavLink to="/page-preset-management">
                    <span>페이지 프리셋</span>
                  </NavLink>
                </li>
              </ul>
            </li>
            <li 
              data-page="device-setting"
              className={isMenuActive('device-setting') ? 'active' : ''}
              onClick={() => handleMenuClick('device-setting')}
            >
              <NavLink to="/device-setting">
                <i className="ri-settings-3-line"></i>
                <span>기기세팅</span>
              </NavLink>
            </li>
            <li 
              data-page="qr-server"
              className={isMenuActive('qr-server') ? 'active' : ''}
              onClick={() => handleMenuClick('qr-server')}
            >
              <NavLink to="/qr-server">
                <i className="ri-server-line"></i>
                <span>QR 서버 관리</span>
              </NavLink>
            </li>
            <li 
              data-page="sales"
              className={isMenuActive('sales') ? 'active' : ''}
              onClick={() => handleMenuClick('sales')}
            >
              <NavLink to="/sales">
                <i className="ri-money-dollar-circle-line"></i>
                <span>매출 관리</span>
              </NavLink>
            </li>
            <li 
              data-page="manual"
              className={isMenuActive('manual') ? 'active' : ''}
              onClick={() => handleMenuClick('manual')}
            >
              <NavLink to="/manual">
                <i className="ri-file-list-2-line"></i>
                <span>매뉴얼페이지</span>
              </NavLink>
            </li>
            <li 
              data-page="software"
              className={isMenuActive('software') ? 'active' : ''}
              onClick={() => handleMenuClick('software')}
            >
              <NavLink to="/software">
                <i className="ri-apps-2-line"></i>
                <span>소프트웨어 관리</span>
              </NavLink>
            </li>
          </ul>
        </nav>
      </div>
      {/*사이드바 끝*/}
    </>
  );
}

